import type { Variants } from "motion/react";

const DURATION_DESKTOP: number = 0.3;

const desktopTransition = {
  duration: DURATION_DESKTOP,
  ease: "easeInOut" as const,
};

/**
 * Animation variants for desktop modal (content and overlay).
 * Content: Fade in with subtle scale effect for a polished entrance.
 * Overlay: Simple fade to match content timing.
 * IMPORTANT: Content includes translate(-50%, -50%) to center the modal since
 * CSS transform is removed to prevent conflicts with Motion.
 */
export const desktopModalVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    x: "-50%",
    y: "-50%",
    transition: desktopTransition,
  },
  animate: {
    opacity: 1,
    scale: 1,
    x: "-50%",
    y: "-50%",
    transition: desktopTransition,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    x: "-50%",
    y: "-50%",
    transition: desktopTransition,
  },
};

/**
 * Animation variants for desktop overlay.
 * Matches desktop modal transition timing.
 */
export const desktopOverlayVariants: Variants = {
  initial: {
    opacity: 0,
    transition: desktopTransition,
  },
  animate: {
    opacity: 1,
    transition: desktopTransition,
  },
  exit: {
    opacity: 0,
    transition: desktopTransition,
  },
};

/**
 * Animation variants for draggable modals.
 * Only animates opacity, not transforms, since dragging handles positioning.
 * Uses desktop timing since draggable modals are desktop-only.
 * NOTE: Centering transforms are handled in StyledContent to work with drag positioning.
 */
export const draggableModalVariants: Variants = {
  initial: {
    opacity: 0,
    transition: desktopTransition,
  },
  animate: {
    opacity: 1,
    transition: desktopTransition,
  },
  exit: {
    opacity: 0,
    transition: desktopTransition,
  },
};

/**
 * Get the appropriate content variants based on context.
 */
export function getContentVariants(isDraggable: boolean): Variants {
  return isDraggable ? draggableModalVariants : desktopModalVariants;
}

/**
 * Get the overlay variants.
 */
export function getOverlayVariants(): Variants {
  return desktopOverlayVariants;
}
